import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { FileText, RefreshCw } from 'lucide-react'
import { useQuotes, useQuote } from '@/hooks/data/useBookings'

function QuotesPage() {
  const { data: quotes = [], isLoading, isError, error, isFetching, refetch } = useQuotes()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const detail = useQuote(selectedId ?? undefined)

  let factors: Record<string, number> | null = null
  if (detail.data) {
    try {
      factors = JSON.parse(detail.data.factorsJson)
    } catch {
      factors = null
    }
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FileText className="w-6 h-6" /> Quotes
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Most recent 100 price quotes (QuotesController). Read-only — quotes are immutable price snapshots.
          </p>
        </div>
        <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isLoading ? (
            <p className="p-6 text-gray-500">Loading quotes…</p>
          ) : isError ? (
            <p className="p-6 text-red-600">Failed to load quotes: {(error as Error)?.message}.</p>
          ) : quotes.length === 0 ? (
            <p className="p-6 text-gray-500">No quotes found.</p>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                <tr>
                  <th className="px-4 py-2">Service</th>
                  <th className="px-4 py-2">Suburb</th>
                  <th className="px-4 py-2">Price</th>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Expires</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => setSelectedId(q.id)}
                    className={`border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      selectedId === q.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{q.serviceType}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{q.suburb}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">${q.price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(q.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(q.expiresAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-1">
          {!selectedId ? (
            <p className="text-sm text-gray-500">Select a quote to see the price breakdown.</p>
          ) : detail.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : detail.data ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800 dark:text-gray-100">{detail.data.serviceType}</h2>
                {detail.data.isExpired && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Expired</span>
                )}
              </div>
              <dl className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <div><span className="text-gray-400">Suburb:</span> {detail.data.suburb}</div>
                <div><span className="text-gray-400">Bedrooms / Bathrooms:</span> {detail.data.bedrooms} / {detail.data.bathrooms}</div>
                <div><span className="text-gray-400">Duration:</span> {detail.data.predictedDurationMinutes} min</div>
                <div><span className="text-gray-400">Add-ons:</span> {detail.data.addOns.length ? detail.data.addOns.join(', ') : '—'}</div>
                <div><span className="text-gray-400">Price:</span> ${detail.data.price.toFixed(2)}</div>
              </dl>
              {factors && Object.keys(factors).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Pricing factors</p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                    {Object.entries(factors).map(([k, v]) => (
                      <li key={k} className="flex justify-between">
                        <span>{k}</span>
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/quotes/')({
  component: QuotesPage,
})
